import React from 'react';
import { Link } from 'react-router-dom';
import {
  Shield, Users, TrendingUp, CheckCircle2, Smartphone, MapPin,
  ArrowRight, Heart, BarChart3, Database, Phone
} from 'lucide-react';

const MajhiMauliSmilePage = () => {
  const impactStats = [
    { value: '100,000+', label: 'Women Empowered Monthly' },
    { value: '800,000+', label: 'Sanitary Pads Distributed' },
    { value: '200+', label: 'Women Agents Employed' },
    { value: '350+', label: 'Villages Covered' }
  ];

  return (
    <div className="min-h-screen" data-testid="mms-page">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-pink-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl" />

        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center gap-8 md:gap-12">
            <img
              src="https://customer-assets-wrfwihn1.emergentagent.net/job_f3f3dd43-5861-424c-bd43-fa6ef34a3548/artifacts/qfc3xy6x_Logo%20Project%20Majhi%20Mauli%20Smile%202.png"
              alt="Majhi Mauli Smile"
              className="w-32 h-32 md:w-40 md:h-40 rounded-2xl object-contain bg-white p-3"
            />
            <div className="text-center md:text-left">
              <div className="inline-flex items-center gap-2 bg-pink-500/20 border border-pink-500/30 text-pink-300 px-4 py-2 rounded-full text-sm font-medium mb-4">
                <Heart className="w-4 h-4" />
                Active Project
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4">
                Majhi Mauli Smile
              </h1>
              <p className="text-xl text-gray-300 mb-4">
                Empowering Women. Building Trust. Transforming Communities.
              </p>
              <div className="flex flex-wrap gap-2 justify-center md:justify-start">
                {['Ramtek', 'Saoner'].map((c, i) => (
                  <span key={i} className="inline-flex items-center gap-1.5 bg-cyan-500/20 text-cyan-400 px-3 py-1 rounded-full text-sm font-medium">
                    <MapPin className="w-3 h-3" />
                    {c} Constituency
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Impact Stats */}
      <section className="py-12 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {impactStats.map((stat, i) => (
              <div key={i} className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-cyan-600">{stat.value}</div>
                <div className="text-sm text-slate-500 mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Mission */}
      <section className="py-20 bg-white" data-testid="mms-mission">
        <div className="max-w-4xl mx-auto px-6">
          <div className="inline-block bg-cyan-50 text-cyan-600 px-4 py-2 rounded-full text-sm font-medium mb-6">
            Our Mission
          </div>
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-gray-900">
            Technology That Serves Rural India
          </h2>
          <div className="space-y-6">
            <p className="text-lg text-gray-600 leading-relaxed">
              Majhi Mauli Smile is our commitment to bringing world-class technology solutions to rural India. We recognize that rural communities have unique needs, challenges, and opportunities that require specialized approaches.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed">
              Through a women-led distribution network, we deliver sanitary pads directly to households across the Ramtek and Saoner constituencies — ensuring dignity, health, and a trusted connection between beneficiaries and their representatives.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed">
              Our approach is built on the foundation of <strong className="text-gray-900">Voter Relationship Management (VRM)</strong> — a comprehensive method that helps elected representatives maintain meaningful, data-driven connections with their constituents through essential service delivery.
            </p>
          </div>
        </div>
      </section>

      {/* Video Section */}
      <section className="py-20 bg-slate-900 relative overflow-hidden" data-testid="mms-video">
        <div className="absolute top-0 left-0 w-96 h-96 bg-pink-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl" />

        <div className="max-w-5xl mx-auto px-6 relative">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 text-pink-400 text-sm font-medium mb-4">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z"/>
              </svg>
              <span className="uppercase tracking-wider">See It In Action</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Majhi Mauli Smile <span className="text-pink-400">On the Ground</span>
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Watch our women agents in action — delivering dignity and building trust across rural communities.
            </p>
          </div>

          <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-slate-800 border border-slate-700">
            <video
              className="w-full"
              controls
              playsInline
              poster=""
            >
              <source src="https://customer-assets-wrfwihn1.emergentagent.net/job_f3f3dd43-5861-424c-bd43-fa6ef34a3548/artifacts/mhckqesl_khaparkheda%20Sakhi.mp4" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
        </div>
      </section>

      {/* The Challenge */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white" data-testid="mms-challenge">
        <div className="max-w-4xl mx-auto px-6">
          <div className="inline-block bg-cyan-50 text-cyan-600 px-4 py-2 rounded-full text-sm font-medium mb-6">
            The Challenge
          </div>
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">
            Access to Essential Hygiene
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed mb-6">
            In rural Maharashtra, access to basic hygiene products like sanitary pads remains a significant challenge. This lack of access leads to serious health complications, social stigma, and limited educational and economic opportunities for women.
          </p>
          <p className="text-lg text-gray-600 leading-relaxed mb-10">
            Traditional distribution methods are inefficient, lack accountability, and fail to create meaningful connections between public servants and constituents.
          </p>

          <div className="grid md:grid-cols-2 gap-6">
            {[
              { icon: Shield, title: 'Health Impact', desc: 'Ensuring every woman has access to safe, quality sanitary products to prevent health complications.' },
              { icon: Users, title: 'Women Empowerment', desc: 'Creating employment for 200+ local women as distribution agents within their own communities.' },
              { icon: Database, title: 'Digital Accountability', desc: 'Every delivery is OTP-verified and digitally tracked — ensuring zero leakage in the supply chain.' },
              { icon: Heart, title: 'Dignity & Trust', desc: 'Door-to-door delivery by trusted local women preserves dignity and builds lasting community trust.' }
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 border border-slate-100 hover:border-cyan-200 hover:shadow-lg transition-all duration-300">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center mb-4">
                  <item.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Technology */}
      <section className="py-20 bg-gradient-to-br from-slate-900 to-slate-800 relative overflow-hidden" data-testid="mms-technology">
        <div className="absolute top-0 left-0 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />

        <div className="max-w-7xl mx-auto px-6 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-white">
              <div className="inline-flex items-center gap-2 text-cyan-400 text-sm font-medium mb-6">
                <Smartphone className="w-4 h-4" />
                <span className="uppercase tracking-wider">Our Technology</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Simple. Accessible. <span className="text-cyan-400">Effective.</span>
              </h2>
              <p className="text-lg text-slate-300 mb-8 leading-relaxed">
                Our mobile app is designed for ease of use by women agents in rural areas. With minimal training, agents can manage deliveries, verify beneficiaries, and sync data — even in low connectivity areas.
              </p>

              <div className="grid grid-cols-2 gap-4 mb-8">
                {[
                  { title: 'OTP Verification', desc: 'Secure delivery confirmation' },
                  { title: 'Real-time Sync', desc: 'Instant dashboard updates' },
                  { title: 'Offline Mode', desc: 'Works without internet' },
                  { title: 'Multi-language', desc: 'Regional language support' }
                ].map((feature, i) => (
                  <div key={i} className="bg-white/5 rounded-xl p-4 border border-white/10">
                    <h4 className="font-semibold text-white mb-1">{feature.title}</h4>
                    <p className="text-sm text-slate-400">{feature.desc}</p>
                  </div>
                ))}
              </div>

              <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-xl p-4">
                <p className="text-cyan-300 text-sm">
                  <strong>User-First Design:</strong> Every screen is optimized for quick navigation, with large touch targets and clear visual hierarchy for ease of use in field conditions.
                </p>
              </div>
            </div>

            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-3xl blur-2xl" />
              <div className="relative">
                <img
                  src="https://customer-assets.emergentagent.com/job_content-hub-780/artifacts/nitbdgm3_Shots%20Mockups%20%283%29.png"
                  alt="Majhi Mauli Smile Mobile App Interface"
                  className="w-full rounded-2xl shadow-2xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Instagram Section */}
      <section className="py-20 bg-white" data-testid="mms-instagram">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 text-pink-500 text-sm font-medium mb-4">
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
              </svg>
              <span className="uppercase tracking-wider">Follow Our Journey</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              @majhimauli.smile
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-8">
              Follow us on Instagram to see daily stories from the field — real deliveries, real impact, real smiles.
            </p>
          </div>

          {/* Instagram Embed */}
          <div className="flex justify-center">
            <div className="w-full max-w-lg bg-white rounded-2xl border border-slate-200 shadow-lg overflow-hidden">
              {/* Profile header */}
              <div className="p-5 flex items-center gap-4 border-b border-slate-100">
                <img
                  src="https://customer-assets-wrfwihn1.emergentagent.net/job_f3f3dd43-5861-424c-bd43-fa6ef34a3548/artifacts/qfc3xy6x_Logo%20Project%20Majhi%20Mauli%20Smile%202.png"
                  alt="Majhi Mauli Smile"
                  className="w-14 h-14 rounded-full object-contain bg-slate-50 p-1"
                />
                <div className="flex-1">
                  <p className="font-bold text-gray-900">majhimauli.smile</p>
                  <p className="text-sm text-gray-500">Majhi Mauli Smile</p>
                </div>
                <a
                  href="https://www.instagram.com/majhimauli.smile?igsh=MTZ5Y20yNDZlOHd6bg%3D%3D&utm_source=qr"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-gradient-to-r from-pink-500 to-purple-600 text-white text-sm font-semibold px-5 py-2 rounded-lg hover:opacity-90 transition-opacity"
                  data-testid="instagram-follow-btn"
                >
                  Follow
                </a>
              </div>

              {/* CTA */}
              <div className="p-6 text-center">
                <p className="text-gray-600 text-sm mb-4">See our latest posts, reels, and stories from the field</p>
                <a
                  href="https://www.instagram.com/majhimauli.smile?igsh=MTZ5Y20yNDZlOHd6bg%3D%3D&utm_source=qr"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-gradient-to-r from-pink-500 via-red-500 to-amber-500 text-white font-semibold px-8 py-3 rounded-xl hover:opacity-90 transition-opacity"
                  data-testid="instagram-visit-btn"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                  </svg>
                  Visit @majhimauli.smile on Instagram
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Want to bring Majhi Mauli Smile to your constituency?
          </h2>
          <p className="text-lg text-gray-300 mb-8 max-w-2xl mx-auto">
            We are expanding to more areas. If you want to make a tangible difference in your constituency, let us talk.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 bg-cyan-500 hover:bg-cyan-400 text-slate-900 font-semibold px-8 py-4 rounded-lg transition-all duration-300"
              data-testid="mms-contact-btn"
            >
              Get in Touch
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/presentation"
              className="inline-flex items-center justify-center gap-2 border border-white/20 text-white hover:bg-white/10 px-8 py-4 rounded-lg transition-all duration-300"
              data-testid="mms-presentation-btn"
            >
              View Full Presentation
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MajhiMauliSmilePage;
